import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Checkbox,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, addDays } from 'date-fns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import apiService from '../../services/apiService';
import PageContainer from '../../components/PageContainer';
import { getRoleDisplayName } from '../../utils/roleUtils';
import { LoadingState } from '../../components/LoadingState';

interface Client {
  id: number;
  agency_name: string;
  url: string;
}

interface Invitation {
  id: number;
  email_address: string;
  client_id: number;
  role: string;
  invitation_code: string;
  used: string | null;
  created: string;
  updated: string;
  used_by: number | null;
  used_by_email: string | null;
  status: 'pending' | 'associated' | 'error';
}

const generateInvitationCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Custom date picker component to avoid type errors
const CustomDatePicker: React.FC<{
  selected: Date;
  onChange: (date: Date | null) => void;
  dateFormat?: string;
  className?: string;
  placeholderText?: string;
}> = ({ selected, onChange, dateFormat, className, placeholderText }) => {
  return (
    <TextField
      fullWidth
      value={format(selected, dateFormat || "MMMM d, yyyy")}
      InputProps={{
        inputComponent: ({ value, onChange: inputOnChange, ...props }) => (
          <DatePicker
            selected={selected}
            onChange={onChange}
            dateFormat={dateFormat || "MMMM d, yyyy"}
            customInput={
              <input
                {...props}
                value={value}
                onChange={inputOnChange}
                style={{
                  width: '100%',
                  height: '40px',
                  padding: '8.5px 14px',
                  border: 'none',
                  background: 'none',
                  outline: 'none',
                  fontSize: '1rem',
                }}
              />
            }
            popperProps={{
              positionFixed: true
            }}
            popperModifiers={[
              {
                name: 'preventOverflow',
                options: {
                  enabled: true,
                  escapeWithReference: false,
                  boundary: window
                },
              },
            ]}
          />
        ),
      }}
      label="Expiration Date"
      size="small"
    />
  );
};

const Invitations: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [selectedInvitations, setSelectedInvitations] = useState<number[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [role, setRole] = useState<string>('owner');
  const [loading, setLoading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsResponse, invitationsResponse] = await Promise.all([
        apiService.get<Client[]>('/api/admin/clients'),
        apiService.get<Invitation[]>('/api/invitations/all/')
      ]);
      setClients(clientsResponse.data);
      setInvitations(invitationsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCreateInvitation = async () => {
    if (!selectedClient || !role) return;

    try {
      setLoading(true);
      const invitation = {
        client_id: selectedClient,
        email_address: email,
        role
      };

      await apiService.post('/api/invitations', invitation);
      await fetchData();
      showSnackbar('Invitation created successfully', 'success');
      
      // Reset form
      setSelectedClient('');
      setEmail('');
      setRole('owner');
    } catch (error) {
      console.error('Error creating invitation:', error);
      showSnackbar('Error creating invitation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    showSnackbar('Invitation code copied to clipboard', 'success');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'associated':
        return {
          bg: 'rgba(46, 160, 67, 0.1)',
          text: '#2ea043'
        };
      case 'pending':
        return {
          bg: 'rgba(251, 191, 36, 0.1)',
          text: '#d97706'
        };
      case 'error':
        return {
          bg: 'rgba(207, 34, 46, 0.1)',
          text: '#cf222e'
        };
      default:
        return {
          bg: 'rgba(101, 116, 139, 0.1)',
          text: '#64748b'
        };
    }
  };

  const handleSelectInvitation = (id: number) => {
    setSelectedInvitations(prev => 
      prev.includes(id) 
        ? prev.filter(invId => invId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedInvitations(invitations.map(inv => inv.id));
    } else {
      setSelectedInvitations([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedInvitations.length) return;

    try {
      setLoading(true);
      await Promise.all(
        selectedInvitations.map(id => 
          apiService.delete(`/api/invitations/${id}`)
        )
      );
      await fetchData();
      setSelectedInvitations([]);
      showSnackbar('Invitations deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting invitations:', error);
      showSnackbar('Error deleting invitations', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Invitations
      </Typography>

      <Paper elevation={2} className="p-6">
        {loading && (
          <LoadingState message="Loading invitations..." containerClassName="min-h-[400px]" />
        )}
        
        {!loading && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box 
              sx={{ 
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                '& > *': {
                  minHeight: '40px',
                }
              }}
            >
              <FormControl size="small" sx={{ width: '25%' }}>
                <InputLabel>Client</InputLabel>
                <Select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value as string)}
                  label="Client"
                >
                  {clients
                    .sort((a, b) => a.agency_name.localeCompare(b.agency_name))
                    .map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.agency_name}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>

              <TextField
                label="Email (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                sx={{ width: '25%' }}
              />

              <FormControl size="small" sx={{ width: '15%' }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={role}
                  onChange={(e) => setRole(e.target.value as string)}
                  label="Role"
                >
                  <MenuItem value="owner">Owner</MenuItem>
                  <MenuItem value="ic">Viewer</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleCreateInvitation}
                disabled={loading || !selectedClient || !role}
                sx={{ height: '40px' }}
              >
                Invite
              </Button>
            </Box>
          </Paper>
        )}

        {/* Invitations Table */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              All Invitations
            </Typography>
            {selectedInvitations.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDeleteSelected}
                disabled={loading}
              >
                Delete Selected ({selectedInvitations.length})
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedInvitations.length > 0 && selectedInvitations.length < invitations.length}
                      checked={selectedInvitations.length === invitations.length && invitations.length > 0}
                      onChange={handleSelectAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Used By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations
                  .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
                  .map((invitation) => {
                    const statusColors = getStatusColor(invitation.status);
                    const client = clients.find(c => c.id === invitation.client_id);

                    return (
                      <TableRow key={invitation.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedInvitations.includes(invitation.id)}
                            onChange={() => handleSelectInvitation(invitation.id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {invitation.invitation_code}
                          {' '}
                          <Tooltip title="Copy invitation code">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyCode(invitation.invitation_code)}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{client?.agency_name}</TableCell>
                        <TableCell>{invitation.email_address}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getRoleDisplayName(invitation.role)} 
                            size="small" 
                            color={invitation.role === 'owner' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {invitation.created ? format(new Date(invitation.created), 'MMM dd, yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            className="px-2 py-1 rounded-full text-sm font-medium"
                            sx={{
                              backgroundColor: statusColors.bg,
                              color: statusColors.text,
                            }}
                          >
                            {invitation.status}
                          </Box>
                        </TableCell>
                        <TableCell>{invitation.used_by_email || '-'}</TableCell>
                      </TableRow>
                    );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Invitations; 